import {
  createNewRepairCosting,
  getAllRepairCostings,
  getRepairCostingById,
  listAllRepairCostings,
  updateRepairCosting,
  updateManyRepairCostings,
  getMatchingRepairCostingError,
  allRepairServicesByEquipmentDamage,
  getTotalCostingByVehiculeCheckId,
} from '@/api/repair-costing/repair-costing';
import { TRepairCostingSchema } from '@/schemas/repair-costing/repair-costing.schema';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notify } from '@/utils/toast.utils';
import { NOTIFY } from '@/utils/constant.utils';
import { RepairCostingFilterType } from '@/entities/repair-costing';
import {
  FindAllRepairServicesByEquipmentDamage,
  FindTotalCostingByVehiculeCheckId,
} from '@/api/entities/repair-costing';

export const useCreateRepairCosting = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TRepairCostingSchema) => createNewRepairCosting(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['getAllRepairCostings'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingRepairCostingError'],
      });
      queryClient.invalidateQueries({
        queryKey: ['listAllRepairCostings'],
      });
      queryClient.invalidateQueries({
        queryKey: ['allRepairServicesByEquipmentDamage'],
      });

      notify(t('repairCostingCreatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useGetMatchingRepairCostingError = (
  query?: TRepairCostingSchema,
) => {
  return useQuery({
    queryKey: ['getMatchingRepairCostingError', query],
    queryFn: () => getMatchingRepairCostingError(query),
  });
};

export const useListAllRepairCostings = () => {
  return useQuery({
    queryKey: ['listAllRepairCostings'],
    queryFn: () => listAllRepairCostings(),
  });
};

export const useGetAllRepairServicesByEquipmentDamage = (
  query?: FindAllRepairServicesByEquipmentDamage,
) => {
  return useQuery({
    queryKey: ['allRepairServicesByEquipmentDamage', query],
    queryFn: () => allRepairServicesByEquipmentDamage(query),
  });
};

export const useGetAllRepairCostings = (
  filter: RepairCostingFilterType,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc',
) => {
  return useQuery({
    queryKey: ['getAllRepairCostings', filter, page, sortBy, sortDirection],
    queryFn: () =>
      getAllRepairCostings(filter, page, limit, sortBy, sortDirection),
  });
};

export const useGetRepairCostingById = (id: string) => {
  return useQuery({
    queryKey: ['getRepairCostingById', id],
    queryFn: () => getRepairCostingById(id),
    enabled: !!id,
  });
};

export const useGetTotalCostingByVehiculeCheckId = (
  query?: FindTotalCostingByVehiculeCheckId,
) => {
  return useQuery({
    queryKey: ['getTotalCostingByVehiculeCheckId', query],
    queryFn: () => getTotalCostingByVehiculeCheckId(query),
  });
};

export const useUpdateRepairCosting = ({
  t,
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (repairCosting: TRepairCostingSchema) =>
      updateRepairCosting(repairCosting),
    onSuccess: (_, variables) => {
      const { id } = variables;

      queryClient.invalidateQueries({
        queryKey: ['getAllRepairCostings'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingRepairCostingError'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getRepairCostingById', id],
      });
      queryClient.invalidateQueries({
        queryKey: ['allRepairServicesByEquipmentDamage'],
      });

      notify(t('repairCostingUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (err: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      handleError?.(err);
      console.error('Error when updating repair costing:', err.error);
    },
  });
};

export const useUpdateManyRepairCostings = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (repairCostings: TRepairCostingSchema[]) =>
      updateManyRepairCostings(repairCostings),
    onSuccess: (_, variables) => {
      for (const item of variables) {
        queryClient.invalidateQueries({
          queryKey: ['getRepairCostingById', item?.id],
        });
      }

      queryClient.invalidateQueries({
        queryKey: ['getAllRepairCostings'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingRepairCostingError'],
      });
      queryClient.invalidateQueries({
        queryKey: ['allRepairServicesByEquipmentDamage'],
      });

      notify(
        variables.length === 0
          ? t('common:theseElementsLinkedToEntitiesNotDeactivable')
          : variables.length > 1
            ? t('repairCostingsUpdatedSuccessfully')
            : t('repairCostingUpdatedSuccessfully'),
        NOTIFY.SUCCESS,
      );

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating repair costings:', error);
    },
  });
};
